import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { ProjectHeader } from "./components/ProjectHeader";
import styles from "./DashBoardProject.module.scss";

import {
  getProjectDoughnutChartData,
  getProjectVerticalBarChartData,
} from "../../../api/projectSummary/projectSummary";
import { useUserData } from "../../../hooks/useUserProfile";
import { DataItem } from "../../../types/previewTemplate";
import { DoughnutChart, DohnutChartPlaceholder } from "../DoughnutChart";
import { VerticalBarChart, VerticalBarChartPlaceholder } from "../VerticalBarChart";


export const DashBoardProject = () => {
  const { organizationId, tenantId } = useUserData();
  const { syncLogId } = useParams();

  const { data: doughnutData, isLoading: isDoughnutLoading } = useQuery({
    queryKey: ["getProjectDoughnutChartData", syncLogId, organizationId, tenantId],
    queryFn: async () => {
      const response = await getProjectDoughnutChartData(
        syncLogId!,
        organizationId,
        tenantId
      );
      return response.data.data;
    },
    enabled: !!tenantId && !!syncLogId,
    gcTime: 5000
  });

  const { data: barChartData, isLoading: isBarChartLoading } = useQuery({
    queryKey: ["getProjectVerticalBarChartData", syncLogId, organizationId, tenantId],
    queryFn: async () => {
      const response = await getProjectVerticalBarChartData(
        syncLogId!,
        organizationId,
        tenantId
      );
      return response.data.data;
    },
    enabled: !!tenantId && !!syncLogId,
    gcTime: 5000
  });

  const isLoading = isDoughnutLoading || isBarChartLoading;

  const { maxDataValue, padding } = useMemo(() => {
    if (barChartData?.data?.length > 0) {
      const maxValue = Math.max(...barChartData.data.map((item: DataItem) => item.value));
      const paddingValue = maxValue * 0.1;
      return { maxDataValue: maxValue, padding: paddingValue };
    }
    return { maxDataValue: 0, padding: 0 };
  }, [barChartData]);

  return (
    <div className={styles.root}>
      <ProjectHeader />
      {isLoading && (
        <div className={styles.barWrapper}>
          <VerticalBarChartPlaceholder />
          <DohnutChartPlaceholder />
        </div>
      )}
      {!isLoading && (
        <div className={styles.barWrapper}>
          <VerticalBarChart
            dataList={barChartData?.data}
            options={{
              responsive: true,
              plugins: {
                datalabels: {
                  color: "black",
                  formatter: (value) =>
                    `${barChartData?.format?.currency}${value.toFixed(barChartData?.format?.decimal)} ${barChartData?.format?.unit}`,
                  labels: {
                    title: {
                      font: {
                        weight: 400,
                        size: 12,
                      },
                    },
                  },
                  anchor: "end",
                  align: -90,
                },
                legend: {
                  display: false,
                },
              },
              scales: {
                y: {
                  max: maxDataValue + padding,
                  ticks: {
                    stepSize: 2,
                    callback: (value) =>
                      `${barChartData?.format?.currency}${(+value).toFixed(barChartData?.format?.decimal)} ${barChartData?.format?.unit}`,
                  },
                  title: {
                    display: true,
                    text: barChartData?.x,
                    color: "#9E9E9E",
                    font: {
                      size: 14,
                      weight: 400,
                    },
                    padding: {
                      bottom: 15,
                    },
                  },
                },

                x: {
                  title: {
                    display: true,
                    text: barChartData?.y,
                    color: "#9E9E9E",
                    font: {
                      size: 14,
                      weight: 400,
                    },
                    padding: {
                      top: 20,
                    },
                  },
                },
              },
              clip: false,
            }}
            title={barChartData?.title}
          />
          <DoughnutChart
            dataList={doughnutData?.data}
            format={doughnutData?.format}
            radius={130}
            className={styles.wrapper}
          />
        </div>
      )}
    </div>
  );
};
